import { axios } from 'api'
import { ClaimStatus } from 'constants/claims'

export { ClaimStatus }
export type ClaimType = 'Exchange' | 'Credit'
export type Delivery =
  | 'Pick Up'
  | 'Curbside Delivery (Up to 50 miles)'
  | 'Garage/Driveway Delivery (Up to 50 miles)'
  | 'Free delivery charges'
  | 'RL Carriers'
  | 'Fedex International'
  | 'Fedex US LTL'
export type ClaimPart =
  | 'Door'
  | 'Drawer Front'
  | 'Frame'
  | 'Line'
  | 'Left Side Panel'
  | 'Right Side Panel'
  | 'Bottom Panel'
  | 'Top Panel'
  | 'Slides'
  | 'Hinge'
  | 'Spinners(Lazy Susan)'
  | 'Shelf Clips'
  | 'Drawer Front for CSB'
  | '2BC'
  | 'Other(Explain in the notes)'
  | 'Drawer Front Bottom'
  | 'Back Panel'

export interface ClaimItem {
  orderItemId: string
  quantity: number
  claimType: ClaimType
  delivery: Delivery
  claimPart: ClaimPart
}

export interface CreateClaimParams {
  orderId: string
  items: ClaimItem[]
}

export interface MessageAttachment {
  fileName: string
  fileUrl: string
}

export interface SendMessageParams {
  message: string
  attachments?: MessageAttachment[]
}

export interface ClaimMessage {
  id: string
  claimId: string
  senderType: 'CUSTOMER' | 'ADMIN'
  senderId: string
  message: string
  attachments: MessageAttachment[]
  createdAt: string
  updatedAt: string
  Account: {
    companyName: string
    email: string
    firstName?: string
    lastName?: string
  }
}

export interface Claim {
  id: string
  orderId: string
  accountId: string
  status: ClaimStatus
  createdAt: string
  updatedAt: string
  Order: {
    id: string
    netsuiteSalesOrderId: string
    createdAt: string
  }
  ClaimItems: Array<{
    id: string
    claimId: string
    orderItemId: string
    quantity: number
    claimType: ClaimType
    delivery: Delivery
    claimPart: ClaimPart
    OrderItem: {
      id: string
      orderId: string
      productId: string
      quantity: number
      unitPrice: number
      isAssembled: boolean
      hingeDirection: string | null
      Product: {
        sku: string
        color: string
        price: number
        imageIndex: number
        isFixedPrice: boolean
      }
    }
  }>
  ClaimMessages?: ClaimMessage[]
}

interface UploadUrlResponse {
  uploadUrl: string
  fileUrl: string
}

export const createClaim = async (
  params: CreateClaimParams
): Promise<{ success: boolean; error?: string; claimId?: string }> => {
  try {
    const response = await axios.post('/claims', params)

    if (response.status === 201) {
      return {
        success: true,
        claimId: response.data.claimId
      }
    } else {
      return {
        success: false,
        error: response.data.error || 'Failed to create claim'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error?.response?.data?.error || 'Failed to create claim'
    }
  }
}

export const sendMessage = async (
  claimId: string,
  params: SendMessageParams
): Promise<{ success: boolean; error?: string; messageId?: string }> => {
  try {
    const response = await axios.post(`/claims/${claimId}/messages`, params)

    if (response.status === 201) {
      return {
        success: true,
        messageId: response.data.messageId
      }
    } else {
      return {
        success: false,
        error: response.data.error || 'Failed to send message'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error?.response?.data?.error || 'Failed to send message'
    }
  }
}

export const getMyClaims = async (): Promise<{
  success: boolean
  claims?: Claim[]
  error?: string
}> => {
  try {
    const response = await axios.get('/claims')

    if (response.status === 200) {
      return {
        success: true,
        claims: response.data.claims
      }
    } else {
      return {
        success: false,
        error: response.data.error || 'Failed to fetch claims'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error?.response?.data?.error || 'Failed to fetch claims'
    }
  }
}

export const getClaimById = async (
  id: string
): Promise<{
  success: boolean
  claim?: Claim
  error?: string
}> => {
  try {
    const response = await axios.get(`/claims/${id}`)

    if (response.status === 200) {
      return {
        success: true,
        claim: response.data.claim
      }
    } else {
      return {
        success: false,
        error: response.data.error || 'Failed to fetch claim'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error?.response?.data?.error || 'Failed to fetch claim'
    }
  }
}

export const getUploadUrl = async (
  fileName: string,
  fileType: string,
  fileSize: number
): Promise<{
  success: boolean
  data?: UploadUrlResponse
  error?: string
}> => {
  try {
    const response = await axios.get('/claims/files/upload', {
      params: { fileName, fileType, fileSize }
    })

    if (response.status === 200) {
      return {
        success: true,
        data: response.data
      }
    } else {
      return {
        success: false,
        error: response.data.error || 'Failed to get upload URL'
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error?.response?.data?.error || 'Failed to get upload URL'
    }
  }
}

export const uploadToS3 = async (
  uploadUrl: string,
  file: File
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type
      }
    })

    if (response.ok) {
      return { success: true }
    } else {
      return {
        success: false,
        error: `S3 upload failed with status ${response.status}`
      }
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to upload to S3'
    }
  }
}

export const uploadFile = async (
  file: File
): Promise<{ success: boolean; fileUrl?: string; error?: string }> => {
  try {
    const uploadUrlResult = await getUploadUrl(file.name, file.type, file.size)

    if (!uploadUrlResult.success || !uploadUrlResult.data) {
      return {
        success: false,
        error: uploadUrlResult.error || 'Failed to get upload URL'
      }
    }

    const { uploadUrl, fileUrl } = uploadUrlResult.data

    const uploadResult = await uploadToS3(uploadUrl, file)

    if (!uploadResult.success) {
      return {
        success: false,
        error: uploadResult.error || 'Failed to upload to S3'
      }
    }

    return {
      success: true,
      fileUrl
    }
  } catch (error: any) {
    return {
      success: false,
      error: error.message || 'Failed to upload file'
    }
  }
}
